'use client';

import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';

import { type Board, type Library } from '@air/api/types';
import { ChevronDownIcon, ClockIcon, SearchIcon, StarIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { GENERAL_LIBRARY_ID, GeneralLibraryTreeItem } from '~/components/tree/GeneralLibraryTreeItem';
import { LibrariesTreeItem } from '~/components/tree/LibrariesTreeItem';
import { SubBoardsTree } from '~/components/tree/SubBoardsTree';
import { TreeLoadMoreRow } from '~/components/tree/TreeLoadMoreRow';
import { TreeStateContextProvider } from '~/contexts/TreeStateContext';
import { type TreePagination } from '~/hooks/useTreePagination';

export type CollapsibleChevronProps = {
  open: boolean;
};

const CollapsibleChevron = memo(({ open }: CollapsibleChevronProps) => (
  <ChevronDownIcon className={cn('transition-transform', !open && '-rotate-90')} size="sm" />
));

CollapsibleChevron.displayName = 'CollapsibleChevron';

/** Navigation button for sidebar menu items */
export type SidebarNavButtonProps = {
  icon: ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
};

const SidebarNavButton = memo(({ icon, label, isActive, onClick }: SidebarNavButtonProps) => (
  <button
    className={cn(
      'flex h-8 items-center gap-2 rounded-6 px-2 font-medium text-14',
      isActive ? 'bg-neutral-4 text-neutral-12' : 'text-neutral-11 hover:bg-neutral-3',
    )}
    onClick={onClick}
    type="button"
  >
    {icon}
    {label}
  </button>
));

SidebarNavButton.displayName = 'SidebarNavButton';

export type SidebarProps = {
  libraries: Library[];
  /**
   * Paging state of the query behind `libraries`, so a workspace with more libraries than one page lists all of them.
   * Omit it to list only the libraries passed in, with no loading row.
   */
  librariesPagination?: TreePagination;
  workspaceId: string;
  /** Handler for library click (including General Library) */
  onLibraryClick: (libraryId: string) => void;
  onBoardClick: (board: Board) => void;
  onBoardMouseEnter?: (board: Board) => void;
  selectedLibraryId?: string;
  selectedBoardId?: string;
  isDisabled?: (board: Board) => boolean;
  /** Workspace selector - render prop for the workspace dropdown */
  workspaceSelector?: ReactNode;
  /** Handler for "Search" (all assets) click */
  onSearchClick?: () => void;
  /** Whether search/all assets view is active */
  isSearchActive?: boolean;
  /** Handler for "My favorites" click */
  onFavoritesClick?: () => void;
  /** Whether favorites view is active */
  isFavoritesActive?: boolean;
  /** Handler for "Recently added" click */
  onRecentClick?: () => void;
  /** Whether recent view is active */
  isRecentActive?: boolean;
  /** Whether to show General Library in the sidebar */
  showGeneralLibrary?: boolean;
  /** Ancestor board IDs for auto-expanding the tree when a nested board is selected */
  selectedBoardAncestorIds?: string[];
  /**
   * CSS variable name for sidebar width. When provided, sidebar width is controlled
   * via this CSS variable (e.g., '--sidebar-width'). This enables performant resizing
   * by updating the CSS variable directly instead of triggering React re-renders.
   * The variable should be set on document.documentElement.
   * Falls back to 256px (w-64) if not provided.
   */
  widthCssVar?: string;
};

export const Sidebar = memo(
  ({
    libraries,
    librariesPagination,
    workspaceId,
    onLibraryClick,
    onBoardClick,
    onBoardMouseEnter,
    selectedLibraryId,
    selectedBoardId,
    isDisabled,
    workspaceSelector,
    onSearchClick,
    isSearchActive,
    onFavoritesClick,
    isFavoritesActive,
    onRecentClick,
    isRecentActive,
    showGeneralLibrary,
    selectedBoardAncestorIds,
    widthCssVar,
  }: SidebarProps) => {
    const [isLibrariesExpanded, setIsLibrariesExpanded] = useState(true);
    // Also true while more pages are coming: a caller that filters `libraries` can get an empty first page with
    // matches further on, and the boards-tree fallback would never load them.
    const hasLibraries = libraries.length > 0 || librariesPagination?.hasNextPage === true;

    const handleToggleLibraries = useCallback(() => {
      setIsLibrariesExpanded((prev) => !prev);
    }, []);

    // Auto-expand selected library and board ancestors when selection changes
    const autoExpandIds = useMemo(() => {
      const ids: string[] = [];

      if (selectedLibraryId) {
        ids.push(selectedLibraryId);
      }
      // Include ancestor board IDs to expand the tree path to the selected board
      if (selectedBoardAncestorIds) {
        ids.push(...selectedBoardAncestorIds);
      }

      return ids;
    }, [selectedLibraryId, selectedBoardAncestorIds]);

    return (
      <TreeStateContextProvider autoExpandIds={autoExpandIds}>
        <aside
          className={cn(
            'sidebar-aside flex h-full min-h-0 shrink-0 flex-col overflow-hidden border-neutral-4 border-r bg-white text-neutral-12 dark:bg-neutral-1',
            !widthCssVar && 'w-64',
          )}
          style={widthCssVar ? { width: `var(${widthCssVar})` } : undefined}
        >
          <div className="w-full border-neutral-4 border-b px-2 py-1">
            {workspaceSelector && <div className="w-fit">{workspaceSelector}</div>}
          </div>

          <nav className="sidebar-primary-nav flex shrink-0 flex-col px-2 pt-3">
            {onSearchClick && (
              <SidebarNavButton
                icon={<SearchIcon size="sm" />}
                isActive={isSearchActive}
                label="Search"
                onClick={onSearchClick}
              />
            )}
            {onFavoritesClick && (
              <SidebarNavButton
                icon={<StarIcon size="sm" />}
                isActive={isFavoritesActive}
                label="My favorites"
                onClick={onFavoritesClick}
              />
            )}
            {onRecentClick && (
              <SidebarNavButton
                icon={<ClockIcon size="sm" />}
                isActive={isRecentActive}
                label="Recently added"
                onClick={onRecentClick}
              />
            )}
          </nav>

          <div className="sidebar-libraries-section mt-3 flex min-h-0 flex-1 flex-col overflow-hidden px-2">
            <button
              aria-expanded={isLibrariesExpanded}
              className="flex h-7 items-center gap-1 px-3 font-semibold text-12 text-neutral-12"
              onClick={handleToggleLibraries}
              type="button"
            >
              {hasLibraries ? 'Libraries' : 'Boards'}
              <CollapsibleChevron open={isLibrariesExpanded} />
            </button>

            {isLibrariesExpanded && (
              <nav className="sidebar-libraries-nav flex min-h-0 flex-1 flex-col overflow-y-auto pt-1 pb-3">
                {hasLibraries ? (
                  <>
                    {showGeneralLibrary && (
                      <GeneralLibraryTreeItem
                        isDisabled={isDisabled}
                        isSelected={selectedLibraryId === GENERAL_LIBRARY_ID}
                        onBoardClick={onBoardClick}
                        onBoardMouseEnter={onBoardMouseEnter}
                        onLibraryClick={onLibraryClick}
                        selectedBoardId={selectedBoardId}
                        workspaceId={workspaceId}
                      />
                    )}
                    {libraries.map((library) => (
                      <LibrariesTreeItem
                        isDisabled={isDisabled}
                        key={library.id}
                        library={library}
                        onBoardClick={onBoardClick}
                        onBoardMouseEnter={onBoardMouseEnter}
                        onLibraryClick={onLibraryClick}
                        selectedBoardId={selectedBoardId}
                        selectedLibraryId={selectedLibraryId}
                        workspaceId={workspaceId}
                      />
                    ))}
                    {librariesPagination && <TreeLoadMoreRow depth={0} pagination={librariesPagination} />}
                  </>
                ) : (
                  <SubBoardsTree
                    depth={0}
                    isDisabled={isDisabled}
                    libraryBoards="ALL"
                    onClick={onBoardClick}
                    onMouseEnter={onBoardMouseEnter}
                    selectedBoardId={selectedBoardId}
                    workspaceId={workspaceId}
                  />
                )}
              </nav>
            )}
          </div>
        </aside>
      </TreeStateContextProvider>
    );
  },
);

Sidebar.displayName = 'Sidebar';
